import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Truck,
  MapPinned,
  Clock,
  BadgeCheck,
  Ship,
  Train,
  Plane,
  Route,
  ArrowLeft
} from 'lucide-react';
import TopStatsNav from '../components/TopStatsNav';

export default function Transport() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f8fafc] font-[IRANSansWeb,Tahoma] p-6 md:p-10" dir="rtl">
      <div className="max-w-6xl mx-auto space-y-8">
        <header className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8 md:p-10 overflow-hidden relative">
          <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.10),transparent_70%)]" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="text-right">
              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-2xl text-[11px] font-extrabold border border-emerald-100">
                <Route size={16} />
                <span>مدیریت حمل‌ونقل و رهگیری چندوجهی</span>
              </div>

              <h1 className="mt-6 text-3xl md:text-4xl font-black text-gray-900 leading-tight">
                حمل و نقل
              </h1>

              <p className="mt-4 text-sm text-gray-500 leading-8">
                در این بخش می‌توانید مسیرهای پیشنهادی را مقایسه کنید، زمان و هزینه‌ی تقریبی را ببینید، نوع حمل را انتخاب کنید
                و وضعیت عملیات را در طول مسیر رهگیری نمایید.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="h-12 px-5 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 font-bold text-sm transition inline-flex items-center justify-center gap-2"
                >
                  <ArrowRight size={18} />
                  بازگشت به داشبورد
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/trade')}
                  className="h-12 px-5 rounded-2xl bg-[#0f766e] hover:bg-[#115e59] text-white font-bold text-sm transition inline-flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/15"
                >
                  بازگشت به تجارت
                  <ArrowLeft size={18} />
                </button>
              </div>
            </div>

            <div className="bg-[#fbfdff] border border-gray-100 rounded-[2.5rem] p-6 shadow-sm flex items-center justify-center overflow-hidden">
              <img
                src="/log.png?v=2"
                alt="تصویر حمل‌ونقل"
                className="w-full h-auto max-h-[320px] object-cover rounded-[1.75rem] shadow-lg shadow-slate-900/10 ring-1 ring-slate-900/5"
                loading="lazy"
              />
            </div>
          </div>
        </header>

        <TopStatsNav
          items={[
            {
              label: 'ثبت و پیگیری پرونده‌های تجاری',
              value: 24,
              unit: 'پرونده فعال',
              route: '/trade',
              accent: 'blue',
              sparkValues: [4, 6, 5, 7, 6, 9]
            },
            {
              label: 'فرصت‌های سرمایه‌گذاری لجستیکی',
              value: 5,
              unit: 'فرصت کوتاه‌مدت',
              route: '/investment',
              accent: 'amber',
              sparkValues: [2, 3, 3, 4, 5, 6]
            }
          ]}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8">
            <div className="flex items-start justify-between gap-6">
              <div className="text-right">
                <h2 className="text-lg font-black text-gray-800">قابلیت‌های حمل‌ونقل</h2>
                <p className="text-[11px] text-gray-400 mt-2 leading-6">
                  انتخاب بهترین مسیر بر اساس هزینه، زمان و نوع حمل. امکان ترکیب چند شیوه (چندوجهی).
                </p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700">
                <Truck size={22} />
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Feature
                icon={<MapPinned size={18} className="text-emerald-700" />}
                title="انتخاب مسیر"
                desc="مقاصد، نقاط تعویض مد، و مسیرهای پیشنهادی را بررسی کنید."
              />
              <Feature
                icon={<Clock size={18} className="text-emerald-700" />}
                title="زمان و هزینه"
                desc="نمایش زمان تقریبی و هزینه‌ی کل با سناریوهای مقایسه‌ای."
              />
              <Feature
                icon={<BadgeCheck size={18} className="text-emerald-700" />}
                title="رهگیری عملیات"
                desc="مشاهده وضعیت مرحله‌ای و اعلان تغییرات در طول مسیر."
              />
              <Feature
                icon={<Route size={18} className="text-emerald-700" />}
                title="حمل چندوجهی"
                desc="ترکیب حمل دریایی/ریلی/جاده‌ای/هوایی برای بهینه‌سازی."
              />
            </div>
          </section>

          <aside className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8">
            <div className="text-right">
              <h3 className="text-lg font-black text-gray-800">مدهای حمل پیشنهادی</h3>
              <p className="text-[11px] text-gray-400 mt-2 leading-6">بر اساس ظرفیت، سرعت و نوع کالا.</p>
            </div>

            <div className="mt-8 space-y-3">
              <Mode title="دریایی" desc="اقتصادی برای بارهای حجیم" icon={<Ship size={18} />} />
              <Mode title="ریلی" desc="پایدار و مناسب ترانزیت" icon={<Train size={18} />} />
              <Mode title="جاده‌ای" desc="انعطاف‌پذیر برای درب-به-درب" icon={<Truck size={18} />} />
              <Mode title="هوایی (کارگو)" desc="سریع برای بارهای حساس" icon={<Plane size={18} />} />
            </div>
          </aside>
        </div>

        <section className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="text-right">
              <h3 className="text-lg font-black text-gray-800">نمونه‌ی سناریو تصمیم‌گیری</h3>
              <p className="text-[11px] text-gray-400 mt-2 leading-6">
                به‌صورت نمونه، سه سناریوی رایج را برای انتخاب مسیر مقایسه می‌کنیم.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/investment')}
              className="h-12 px-6 rounded-2xl bg-yellow-400 hover:bg-yellow-500 text-[#1A3062] font-black transition inline-flex items-center gap-2 shadow-lg"
            >
              مشاهده سرمایه‌گذاری
              <ArrowLeft size={18} />
            </button>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Scenario
              title="اقتصادی"
              badge="هزینه کمتر"
              time="۸–۱۰ روز"
              cost="کم"
              note="ترکیب دریایی + ریلی"
            />
            <Scenario
              title="متعادل"
              badge="زمان/هزینه"
              time="۵–۷ روز"
              cost="متوسط"
              note="ریلی + جاده‌ای"
            />
            <Scenario
              title="سریع"
              badge="زمان کمتر"
              time="۲–۳ روز"
              cost="بالا"
              note="هوایی (کارگو) + جاده‌ای"
            />
          </div>
        </section>
      </div>
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-[#fbfdff] p-5 hover:shadow-sm transition">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
          {icon}
        </div>
        <div className="text-sm font-extrabold text-gray-800">{title}</div>
      </div>
      <div className="text-[11px] text-gray-400 mt-2 leading-6">{desc}</div>
    </div>
  );
}

function Mode({ title, desc, icon }) {
  return (
    <div className="flex items-center justify-between gap-4 p-4 rounded-2xl border border-gray-100 bg-[#fbfdff] hover:shadow-sm transition">
      <div className="text-right min-w-0">
        <div className="text-sm font-extrabold text-gray-800">{title}</div>
        <div className="text-[11px] text-gray-400 mt-1 leading-6">{desc}</div>
      </div>
      <div className="w-10 h-10 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700 flex-shrink-0">
        {icon}
      </div>
    </div>
  );
}

function Scenario({ title, badge, time, cost, note }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-black text-gray-800">{title}</div>
        <div className="text-[10px] font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-100 px-3 py-1.5 rounded-full">
          {badge}
        </div>
      </div>
      <div className="mt-4 space-y-2 text-[12px] text-gray-600">
        <Row label="زمان" value={time} />
        <Row label="هزینه" value={cost} />
        <Row label="ترکیب" value={note} />
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="text-gray-400 font-bold">{label}</div>
      <div className="font-extrabold text-gray-800">{value}</div>
    </div>
  );
}

